/**
 * Input Sanitization Utilities
 * Provides per-field validation and filtering for XSS prevention
 */

// Character filter patterns (for blocking disallowed chars)
// These patterns match characters that should be REMOVED
export const DISALLOWED_CHARS = {
  USERNAME: /[^a-zA-Z0-9_.@\-]/g,
  GENERAL_TEXT: /[^a-zA-Z0-9\-_.\s]/g,
  EMAIL: /[^a-zA-Z0-9@.\-_+]/g,
  URL: /[^a-zA-Z0-9:/?#[\]@!$&'()*+,;=.\-_%]/g,
  IDENTIFIER: /[^a-zA-Z0-9\-_]/g,
  NUMERIC: /[^0-9]/g,
};

// Validation patterns (for checking if value is valid)
export const FIELD_PATTERNS = {
  USERNAME: /^[a-zA-Z0-9_.@\-]*$/,
  GENERAL_TEXT: /^[a-zA-Z0-9\-_.\s]*$/,
  EMAIL: /^[a-zA-Z0-9@.\-_+]*$/,
  URL: /^[a-zA-Z0-9:/?#[\]@!$&'()*+,;=.\-_%]*$/,
  IDENTIFIER: /^[a-zA-Z0-9\-_]*$/,
  NUMERIC: /^[0-9]*$/,
  JSON: null, // No character filter, validate structure only
  PASSWORD: null, // No character filter
};

// User-friendly messages for each field type
export const FIELD_MESSAGES = {
  USERNAME: 'Only letters, numbers, underscores, periods, hyphens, and @ symbols are allowed',
  GENERAL_TEXT: 'Only letters, numbers, spaces, hyphens, underscores, and periods are allowed',
  EMAIL: 'Only valid email characters are allowed',
  URL: 'Only valid URL characters are allowed',
  IDENTIFIER: 'Only letters, numbers, hyphens, and underscores are allowed',
  NUMERIC: 'Only numbers are allowed',
};

// Field name to field type mappings
export const FIELD_TYPE_MAPPINGS = {
  // User fields
  username: 'USERNAME',
  email: 'EMAIL',
  firstName: 'GENERAL_TEXT',
  lastName: 'GENERAL_TEXT',

  // Group fields
  groupName: 'GENERAL_TEXT',
  name: 'GENERAL_TEXT',

  // Config fields
  configName: 'GENERAL_TEXT',
  description: 'GENERAL_TEXT',
  displayName: 'GENERAL_TEXT',
  accountName: 'GENERAL_TEXT',

  // IdP fields - clientId uses GENERAL_TEXT to support OAuth client IDs with dots (e.g., xxx.apps.googleusercontent.com)
  clientId: 'GENERAL_TEXT',
  clientSecret: 'PASSWORD',
  authServerUrl: 'URL',
  authorizationUrl: 'URL',
  tokenUrl: 'URL',
  userInfoUrl: 'URL',
  logoutUrl: 'URL',
  issuer: 'URL',
  tenantId: 'IDENTIFIER',
  providerName: 'IDENTIFIER',

  // Permission set fields
  permissionSetName: 'GENERAL_TEXT',
  policyName: 'GENERAL_TEXT',
  inlinePolicy: 'JSON',

  // Customer fields
  realmName: 'IDENTIFIER',

  // Auth fields
  password: 'PASSWORD',
  currentPassword: 'PASSWORD',
  newPassword: 'PASSWORD',
  totpCode: 'USERNAME', // Digits only in practice
};

/**
 * Filters out disallowed characters from input
 * @param {string} value - The input value
 * @param {string} fieldType - The field type (USERNAME, GENERAL_TEXT, etc.)
 * @returns {string} - Filtered value
 */
export const filterInput = (value, fieldType) => {
  if (value === null || value === undefined) return '';
  if (!DISALLOWED_CHARS[fieldType]) return value;
  return value.replace(DISALLOWED_CHARS[fieldType], '');
};

/**
 * Validates input against field pattern
 * @param {string} value - The input value
 * @param {string} fieldType - The field type
 * @returns {boolean} - True if valid
 */
export const isValidInput = (value, fieldType) => {
  if (!value) return true;
  if (!FIELD_PATTERNS[fieldType]) return true; // No pattern = all allowed
  return FIELD_PATTERNS[fieldType].test(value);
};

/**
 * Gets error message for invalid input
 * @param {string} fieldType - The field type
 * @returns {string} - Error message
 */
export const getInputErrorMessage = (fieldType) => {
  return FIELD_MESSAGES[fieldType] || 'Invalid characters detected';
};

/**
 * Sanitizes and trims input
 * @param {string} value - The input value
 * @param {string} fieldType - The field type
 * @returns {{ value: string, wasModified: boolean }}
 */
export const sanitizeInput = (value, fieldType) => {
  if (!value) return { value: '', wasModified: false };
  const filtered = filterInput(value, fieldType);
  const trimmed = filtered.trim();
  return {
    value: trimmed,
    wasModified: filtered !== value,
  };
};

/**
 * Creates a filtered onChange handler for form fields
 * @param {function} setter - State setter function (e.g., setFormData)
 * @param {string} fieldName - The field name in the form state
 * @param {string} fieldType - The field type for sanitization
 * @returns {function} - Event handler function
 */
export const createFilteredHandler = (setter, fieldName, fieldType) => {
  return (e) => {
    const filteredValue = filterInput(e.target.value, fieldType);
    setter(prev => ({ ...prev, [fieldName]: filteredValue }));
  };
};

/**
 * Gets the field type for a given field name
 * @param {string} fieldName - The field name
 * @returns {string} - The field type (defaults to GENERAL_TEXT)
 */
export const getFieldType = (fieldName) => {
  return FIELD_TYPE_MAPPINGS[fieldName] || 'GENERAL_TEXT';
};

/**
 * Validates JSON structure
 * @param {string} jsonString - The JSON string to validate
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateJSON = (jsonString) => {
  if (!jsonString || !jsonString.trim()) {
    return { isValid: true, error: null };
  }
  try {
    JSON.parse(jsonString);
    return { isValid: true, error: null };
  } catch (e) {
    return { isValid: false, error: 'Invalid JSON format' };
  }
};
